import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { Plus } from 'lucide-react';
import { Button } from './ui/button';
import { CancelButton } from './ui/cancel-button';
import { RotatingDeleteButton } from './RotatingDeleteButton';
import { PermissionSelectItem, permissionSelectContentClassName } from './PermissionSelectItem';
import { useLocale } from '../contexts/LocaleContext';
import { useToast } from '../contexts/ToastContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogPrimaryShortcut,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectTrigger,
} from './ui/select';
import { Input } from './ui/input';

type ProjectSharePermission = 'reader' | 'commenter' | 'editor' | 'admin';

interface ProjectShare {
  id: string;
  userEmail: string;
  permission: ProjectSharePermission;
  createdAt: string;
}

interface ShareProjectProps {
  token: string | null;
  projectId: string | undefined;
  t: (key: string) => string;
  showToast: (message: string) => void;
}

export const ShareProject: React.FC<ShareProjectProps> = ({
  token,
  projectId,
  t,
  showToast,
}) => {
  const { locale } = useLocale();
  const { showErrorToast } = useToast();
  const [projectShares, setProjectShares] = useState<ProjectShare[]>([]);
  const [isLoadingShares, setIsLoadingShares] = useState<boolean>(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [shareEmail, setShareEmail] = useState<string>('');
  const [sharePermission, setSharePermission] = useState<ProjectSharePermission>('reader');
  const [isSharing, setIsSharing] = useState<boolean>(false);

  useEffect(() => {
    const fetchProjectShares = async (): Promise<void> => {
      if (!token || !projectId || projectId === 'new') {
        return;
      }

      setIsLoadingShares(true);
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/shares`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setProjectShares(data.shares || []);
        } else {
          console.error('Failed to load project shares');
        }
      } catch (err) {
        console.error('Error fetching project shares:', err);
      } finally {
        setIsLoadingShares(false);
      }
    };

    fetchProjectShares();
  }, [token, projectId]);

  const permissionLabel = (permission: ProjectSharePermission): string => {
    if (permission === 'reader') return t('permission_reader') || 'Reader';
    if (permission === 'commenter') return t('permission_commenter') || 'Commenter';
    if (permission === 'editor') return t('permission_editor') || 'Editor';
    return t('permission_admin') || 'Admin';
  };

  const handleShareWithUser = async (): Promise<void> => {
    if (!token || !projectId || projectId === 'new' || !shareEmail || isSharing) {
      return;
    }

    if (!shareEmail.includes('@')) {
      showErrorToast(t('invalid_email') || 'Invalid email address');
      return;
    }

    setIsSharing(true);
    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/share`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userEmail: shareEmail,
          permission: sharePermission,
          locale: locale,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProjectShares((prev) => [data.share, ...prev]);
        setShareEmail('');
        setSharePermission('reader');
        setIsShareModalOpen(false);
        showToast(t('project_shared') || 'Project shared successfully');
      } else {
        const errorData = await response.json().catch(() => ({}));
        showErrorToast(errorData.error || t('failed_to_share_project') || 'Failed to share project');
      }
    } catch (err) {
      console.error('Error sharing project:', err);
      showErrorToast(t('failed_to_share_project') || 'Failed to share project');
    } finally {
      setIsSharing(false);
    }
  };

  const handleDeleteShare = async (shareId: string): Promise<void> => {
    if (!token || !projectId || projectId === 'new') {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/shares/${shareId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setProjectShares((prev) => prev.filter((share) => share.id !== shareId));
        showToast(t('share_removed') || 'Share removed');
      } else {
        showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
      }
    } catch (err) {
      console.error('Error deleting share:', err);
      showErrorToast(t('failed_to_remove_share') || 'Failed to remove share');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', gap: '0.75rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--foreground)' }}>{t('share_project_with_users')}</h3>
        <Button
          onClick={() => setIsShareModalOpen(true)}
          disabled={!token || !projectId || projectId === 'new'}
          size="sm"
        >
          <Plus className="h-4 w-4" />
          {t('share_project_with_new_user')}
        </Button>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-6 md:grid-cols-2 md:items-start [&>*]:min-w-0">
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)', lineHeight: 1.5 }}>
          {t('share_project_with_users_description')}
        </p>

        {isLoadingShares ? (
          <div>{t('loading')}</div>
        ) : projectShares.length === 0 ? (
          <div style={{ color: 'var(--muted-foreground)', fontSize: '0.875rem' }}>{t('no_project_shares') || 'No users have been shared with this project'}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {projectShares.map((share) => (
              <div
                key={share.id}
                style={{
                  padding: '0.75rem',
                  borderRadius: '0.5rem',
                  backgroundColor: 'var(--card)',
                  border: '1px solid var(--border)',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)', marginBottom: '0.25rem', overflowWrap: 'anywhere' }}>
                    {share.userEmail}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted-foreground)' }}>
                    {permissionLabel(share.permission)}
                  </div>
                </div>
                <RotatingDeleteButton
                  onClick={() => handleDeleteShare(share.id)}
                  tooltipText={t('remove') || 'Remove'}
                  ariaLabel={t('remove') || 'Remove'}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={isShareModalOpen} onOpenChange={setIsShareModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('share_project_with_new_user')}</DialogTitle>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', paddingTop: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('email') || 'Email'}
              </label>
              <Input
                type="email"
                value={shareEmail}
                onChange={(e) => setShareEmail(e.target.value)}
                placeholder={t('user_email_placeholder') || 'user@example.com'}
                autoFocus
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: 500, color: 'var(--foreground)' }}>
                {t('permission') || 'Permission'}
              </label>
              <Select value={sharePermission} onValueChange={(value: ProjectSharePermission) => setSharePermission(value)}>
                <SelectTrigger>
                  <span className="truncate">{permissionLabel(sharePermission)}</span>
                </SelectTrigger>
                <SelectContent className={permissionSelectContentClassName}>
                  <PermissionSelectItem
                    value="reader"
                    label={t('permission_reader') || 'Reader'}
                    description={t('permission_project_reader_desc') || 'Can see everything but cannot edit'}
                  />
                  <PermissionSelectItem
                    value="commenter"
                    label={t('permission_commenter') || 'Commenter'}
                    description={t('permission_project_commenter_desc') || 'Can see everything and comment on documents'}
                  />
                  <PermissionSelectItem
                    value="editor"
                    label={t('permission_editor') || 'Editor'}
                    description={t('permission_project_editor_desc') || 'Can see and edit everything except sharing'}
                  />
                  <PermissionSelectItem
                    value="admin"
                    label={t('permission_admin') || 'Admin'}
                    description={t('permission_project_admin_desc') || 'Can see, edit, and manage sharing'}
                  />
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="mt-6">
            <CancelButton onClick={() => setIsShareModalOpen(false)}>
              {t('cancel') || 'Cancel'}
            </CancelButton>
            <Button
              onClick={handleShareWithUser}
              disabled={!shareEmail || isSharing}
              dialogPrimary
              className="inline-flex items-center gap-1.5"
            >
              {isSharing ? (
                t('sharing') || 'Sharing...'
              ) : (
                <>
                  {t('share_project')}
                  <DialogPrimaryShortcut className="ml-0.5" />
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
